class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClose();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.boundClose === 'true') return;
    overlay.dataset.boundClose = 'true';
    overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.cartDrawerBound === 'true') return;
    cartLink.dataset.cartDrawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.documentElement.classList.add('egj-cart-open');
      document.body.classList.add('egj-cart-open');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const emptyContainer = this.querySelector('.drawer__inner-empty');
        const drawerRoot = document.getElementById('CartDrawer') || this.querySelector('.drawer__inner') || this;
        const containerToTrapFocusOn = this.classList.contains('is-empty') && emptyContainer
          ? emptyContainer
          : drawerRoot;
        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close') ||
          emptyContainer?.querySelector('a') ||
          containerToTrapFocusOn;

        if (containerToTrapFocusOn && typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.documentElement.classList.remove('egj-cart-open');
    document.body.classList.remove('egj-cart-open');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Host + items own is-empty (EGJ CSS). Clear before section replace so the
    // first ATC from an empty cart reveals header/items/footer immediately.
    this.classList.remove('is-empty');
    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;

      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHtml) return;

      const html = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (html === null) return;

      sectionElement.innerHTML = html;
    });

    setTimeout(() => {
      this.bindOverlayClose();
      this.setHeaderCartIconAccessibility();
      if (window.EgjLoading && typeof window.EgjLoading.bindAll === 'function') {
        window.EgjLoading.bindAll(this);
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  enableLoading(line) {
    super.enableLoading(line);
    this.classList.add('is-updating');
  }

  disableLoading() {
    super.disableLoading();
    this.classList.remove('is-updating');
    const drawer = this.closest('cart-drawer');
    if (window.EgjLoading && typeof window.EgjLoading.bindAll === 'function') {
      window.EgjLoading.bindAll(drawer || this);
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
